import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

export default function SignIn() {
  const { signIn }  = useAuth();
  const navigate    = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [loadMsg, setLoadMsg]   = useState('Signing in…');
  const timerRef = useRef(null);
  const timer2Ref = useRef(null);

  // Smart loading messages — reassure user if it takes longer than expected
  const startLoadingMessages = () => {
    timerRef.current = setTimeout(() => {
      setLoadMsg('Almost there — connecting to server…');
    }, 4000);
    timer2Ref.current = setTimeout(() => {
      setLoadMsg('Taking a little longer than usual — please wait…');
    }, 9000);
  };

  const clearTimers = () => {
    clearTimeout(timerRef.current);
    clearTimeout(timer2Ref.current);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLoadMsg('Signing in…');
    startLoadingMessages();

    try {
      await signIn(email, password);
      clearTimers();
      navigate('/dashboard');
    } catch (err) {
      clearTimers();
      const msg = err?.message || '';
      if (
        msg.toLowerCase().includes('invalid') ||
        msg.toLowerCase().includes('credentials') ||
        msg.toLowerCase().includes('wrong password') ||
        msg.toLowerCase().includes('user not found')
      ) {
        setError('Incorrect email or password. Please check and try again.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        setError('Please confirm your email first. Check your inbox for the confirmation link.');
      } else if (msg.toLowerCase().includes('network') || msg.toLowerCase().includes('fetch')) {
        setError('Network error. Please check your internet connection and try again.');
      } else if (msg.toLowerCase().includes('rate limit')) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError('Could not sign in. Please check your details and try again.');
      }
    }
    setLoading(false);
    setLoadMsg('Signing in…');
  };

  // Cleanup timers on unmount
  useEffect(() => () => clearTimers(), []);

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ marginBottom:28 }}>
          <Logo size={36} showText={true} textSize={18} />
        </div>
        <h1 style={s.title}>Welcome back</h1>
        <p style={s.sub}>Sign in to your account to continue</p>

        {error && (
          <div style={s.errorBox}>
            <div>{error}</div>
            <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid #fecdd3' }}>
              <span
                style={{ fontSize:12, color:'#be123c', fontWeight:700, cursor:'pointer', textDecoration:'underline' }}
                onClick={() => { setError(''); setLoading(false); }}
              >
                Try again
              </span>
              {' · '}
              <span
                style={{ fontSize:12, color:'#be123c', fontWeight:700, cursor:'pointer', textDecoration:'underline' }}
                onClick={() => window.location.reload()}
              >
                Refresh page
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>Email address</label>
            <input
              style={{ ...s.input, borderColor: loading ? '#c7d2fe' : '#e2e8f0' }}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={loading}
              required
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Password</label>
            <input
              style={{ ...s.input, borderColor: loading ? '#c7d2fe' : '#e2e8f0' }}
              type="password"
              placeholder="Your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
              required
            />
          </div>
          <div style={{ textAlign:'right', marginTop:-10 }}>
            <span
              style={{ fontSize:12, color:'#6366f1', fontWeight:600, cursor:'pointer', fontFamily:'sans-serif' }}
              onClick={() => navigate('/forgot-password')}
            >
              Forgot password?
            </span>
          </div>

          <button
            style={{ ...s.btn, opacity: loading ? 0.85 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                <span style={{ display:'inline-block', width:16, height:16, border:'2px solid #ffffff60', borderTop:'2px solid #fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                {loadMsg}
              </span>
            ) : 'Sign In →'}
          </button>
        </form>

        {/* Helpful note for slow connections */}
        {loading && (
          <div style={{ textAlign:'center', marginTop:14, fontSize:12, color:'#94a3b8', fontFamily:'sans-serif' }}>
            First sign-in after a while may take up to 15 seconds — please don't close this page.
          </div>
        )}

        <p style={s.footer}>
          Don't have an account?{' '}
          <Link to="/signup" style={s.link}>Create one free</Link>
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const s = {
  page:    { minHeight:'100vh', background:'linear-gradient(160deg,#f8faff,#eef2ff)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  card:    { background:'#fff', borderRadius:20, border:'1px solid #e2e8f0', padding:'40px 36px', width:'100%', maxWidth:440, boxShadow:'0 4px 40px rgba(99,102,241,0.08)' },
  title:   { fontSize:24, fontWeight:700, color:'#0f172a', marginBottom:8 },
  sub:     { fontSize:14, color:'#64748b', fontFamily:'sans-serif', marginBottom:28 },
  errorBox:{ background:'#fff1f2', border:'1px solid #fecdd3', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#be123c', fontFamily:'sans-serif', marginBottom:18 },
  form:    { display:'flex', flexDirection:'column', gap:18 },
  field:   { display:'flex', flexDirection:'column', gap:7 },
  label:   { fontSize:13, fontWeight:700, color:'#334155', fontFamily:'sans-serif' },
  input:   { padding:'13px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, color:'#1e293b', outline:'none', fontFamily:'sans-serif', background:'#fafbff', transition:'border-color 0.2s' },
  btn:     { padding:'14px', borderRadius:10, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:15, fontWeight:700, fontFamily:'sans-serif', marginTop:4 },
  footer:  { textAlign:'center', fontSize:13, color:'#94a3b8', fontFamily:'sans-serif', marginTop:22 },
  link:    { color:'#6366f1', fontWeight:700, textDecoration:'none' },
};